import { lazy, Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { QUERY_DEFAULT_OPTIONS } from '@/lib/queryConfig'
import { ConfigProvider, App as AntApp, Spin } from 'antd'
import itIT from 'antd/locale/it_IT'
import dayjs from 'dayjs'
import 'dayjs/locale/it'
import { authApi } from '@/api/auth'

// ── Componenti critici: statici (necessari subito) ──────────
import AppLayout from '@/components/Layout/AppLayout'
import LoginPage from '@/pages/Login/LoginPage'
import DashboardPage from '@/pages/Dashboard/DashboardPage'
import MobileLayout from '@/pages/Mobile/MobileLayout'
import { useAuthStore } from '@/store/authStore'

// ── Lazy: pagine pesanti caricate on-demand ─────────────────
const ClientiListPage = lazy(() => import('@/pages/Clienti/ClientiListPage'))
const ClienteDetailPage = lazy(() => import('@/pages/Clienti/ClienteDetailPage'))
const ImpiantiPage = lazy(() => import('@/pages/Impianti/ImpiantiPage'))
const DigitalTwinPage = lazy(() => import('@/pages/Impianti/DigitalTwinPage'))
const InterventiPage = lazy(() => import('@/pages/Interventi/InterventiPage'))
const TicketPage = lazy(() => import('@/pages/Ticket/TicketPage'))
const PuntiVenditaPage = lazy(() => import('@/pages/PuntiVendita/PuntiVenditaPage'))
const GpsPage = lazy(() => import('@/pages/GPS/GpsPage'))
const TerzistiPage = lazy(() => import('@/pages/Terzisti/TerzistiPage'))
const PostInterventoPage = lazy(() => import('@/pages/PostIntervento/PostInterventoPage'))
const FatturazionePage = lazy(() => import('@/pages/Fatturazione/FatturazioneePage'))
const AnalyticsPage = lazy(() => import('@/pages/Analytics/AnalyticsPage'))
const ConfigurazionePage = lazy(() => import('@/pages/Configurazione/ConfigurazionePage'))
const ProspectPage = lazy(() => import('@/pages/Prospect/ProspectPage'))
const ContenutiFunnelPage = lazy(() => import('@/pages/ContenutiFunnel/ContenutiFunnelPage'))
const IntakeAIPage = lazy(() => import('@/pages/IntakeAI/IntakeAIPage'))
const ContrattiPage = lazy(() => import('@/pages/Contratti/ContrattiPage'))
const ManualPage = lazy(() => import('@/pages/Manuale/ManualPage'))
const ArchivioPage = lazy(() => import('@/pages/Archivio/ArchivioPage'))
const ScadenziarioAnalisiPage = lazy(() => import('@/pages/Clienti/ScadenziarioAnalisiPage'))
const CostiPage = lazy(() => import('@/pages/Costi/CostiPage'))
const MobileInterventiList = lazy(() => import('@/pages/Mobile/MobileInterventiList'))
const MobileInterventoDetail = lazy(() => import('@/pages/Mobile/MobileInterventoDetail'))
const MobileFirmaCliente = lazy(() => import('@/pages/Mobile/MobileFirmaCliente'))
const PreventivoPubblicoPage = lazy(() => import('@/pages/Pubblico/PreventivoPubblicoPage'))
const AiChatPage = lazy(() => import('@/pages/AiChat/AiChatPage'))
const PreventivatorePage = lazy(() => import('@/pages/Preventivatore/PreventivatorePage'))
const PianificazionePage = lazy(() => import('@/pages/Pianificazione/PianificazionePage'))
const PostaInUscitaPage = lazy(() => import('@/pages/Notifiche/PostaInUscitaPage'))
const PostaInArrivoPage = lazy(() => import('@/pages/Notifiche/PostaInArrivoPage'))

dayjs.locale('it')

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
    <Spin size="large" tip="Caricamento..." />
  </div>
)

const queryClient = new QueryClient({ defaultOptions: QUERY_DEFAULT_OPTIONS })

function RequireAuth({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const bootstrapped = useAuthStore((s) => s.bootstrapped)
  const utente = useAuthStore((s) => s.utente)
  const location = useLocation()

  if (!bootstrapped) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />

  if (utente?.ruoli?.includes('terzista') && !location.pathname.startsWith('/m')) {
    return <Navigate to="/m/interventi" replace />
  }

  return <>{children}</>
}

function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const setUtente = useAuthStore((s) => s.setUtente)
  const clearAuth = useAuthStore((s) => s.clearAuth)
  const bootstrapped = useAuthStore((s) => s.bootstrapped)

  useEffect(() => {
    if (bootstrapped) return
    ;(async () => {
      try {
        const u = await authApi.me()
        setUtente(u)
      } catch {
        try {
          await authApi.refresh()
          const u = await authApi.me()
          setUtente(u)
        } catch {
          clearAuth()
        }
      }
    })()
  }, [bootstrapped, setUtente, clearAuth])

  return <>{children}</>
}

export default function App() {
  return (
    <ConfigProvider
      locale={itIT}
      getPopupContainer={(trigger) => (trigger?.closest?.('.ant-modal-body') as HTMLElement) || document.body}
      theme={{
        token: {
          colorPrimary: '#1a3a6b',
          borderRadius: 8,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
      }}
    >
      <AntApp>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <AuthBootstrap>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <RequireAuth>
                    <AppLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<DashboardPage />} />
                <Route path="ai-chat"          element={<AiChatPage />} />
                <Route path="preventivatore"  element={<PreventivatorePage />} />
                <Route path="prospect"        element={<ProspectPage />} />
                <Route path="contenuti-funnel" element={<ContenutiFunnelPage />} />
                <Route path="clienti"         element={<ClientiListPage />} />
                <Route path="clienti/:id"     element={<ClienteDetailPage />} />
                <Route path="contratti"       element={<ContrattiPage />} />
                <Route path="pianificazione"  element={<PianificazionePage />} />
                <Route path="impianti"        element={<ImpiantiPage />} />
                <Route path="punti-vendita"   element={<PuntiVenditaPage />} />
                <Route path="analisi-rifiuti" element={<ScadenziarioAnalisiPage />} />
                <Route path="intake-ai"   element={<IntakeAIPage />} />
                <Route path="ticket"          element={<TicketPage />} />
                <Route path="interventi"      element={<InterventiPage />} />
                <Route path="terzisti"        element={<TerzistiPage />} />
                <Route path="gps"             element={<GpsPage />} />
                <Route path="post-intervento" element={<PostInterventoPage />} />
                <Route path="fatturazione"    element={<FatturazionePage />} />
                <Route path="analytics"       element={<AnalyticsPage />} />
                <Route path="costi"           element={<CostiPage />} />
                <Route path="configurazione"  element={<ConfigurazionePage />} />
                <Route path="manuale"        element={<ManualPage />} />
                <Route path="archivio"        element={<ArchivioPage />} />
                <Route path="posta-in-arrivo" element={<PostaInArrivoPage />} />
                <Route path="posta-in-uscita" element={<PostaInUscitaPage />} />
              </Route>
              <Route
                path="/m"
                element={
                  <RequireAuth>
                    <MobileLayout />
                  </RequireAuth>
                }
              >
                <Route index element={<Navigate to="/m/interventi" replace />} />
                <Route path="interventi" element={<MobileInterventiList />} />
                <Route path="interventi/:id" element={<MobileInterventoDetail />} />
                <Route path="interventi/:id/firma" element={<MobileFirmaCliente />} />
              </Route>
              <Route path="/impianti/qr/:uuid" element={<DigitalTwinPage />} />
              <Route path="/p/preventivo/:token" element={<PreventivoPubblicoPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
            </AuthBootstrap>
          </BrowserRouter>
        </QueryClientProvider>
      </AntApp>
    </ConfigProvider>
  )
}
